import { useFormikEnhanced } from '@superdispatch/forms';
import { useSnackbarStack } from '@superdispatch/ui';
import { Button } from '@superdispatch/ui-lab';
import { APIError } from 'shared/api/APIError';
import { APIResponse } from 'shared/api/CarrierAPIClient';
import { isWebView } from 'shared/constants/AppTypeConstants';
import { useInitialValues } from 'shared/form/AppFormik';
import { toFormikValidationSchema } from 'shared/form/FormSchema';
import { useFlaggedSchema } from 'shared/hooks/useFlaggedSchema';
import { DocumentTitle } from 'shared/layout/DocumentTitle';
import { ProfileStateMotorCarrierPermitForm } from 'shared/modules/carrier-profile/core/ProfileBusinessLicenseForm';
import { trackCarrierProfileEvent } from 'shared/modules/carrier-profile/data/CarrierProfileAnalytics';
import {
  useBusinessLicense,
  useCarrierProfileAPI,
} from 'shared/modules/carrier-profile/data/CarrierProfileAPI';
import {
  BusinessLicenseDTO,
  businessLicenseSchema,
  businessLicenseYupSchema,
} from 'shared/modules/carrier-profile/data/CarrierProfileDTO';
import {
  useSubmitVerificationDocument,
  VerificationDialogs,
} from './core/VerificationDialogs';
import { ProfilePageLayout } from './ProfilePageLayout';
import { useEffect, useMemo } from 'react';

export function ProfileBusinessLicensePage() {
  const { addSnackbar } = useSnackbarStack();
  const { updateBusinessLicense } = useCarrierProfileAPI();
  const { data, isLoading, isError } = useBusinessLicense();
  const schema = useFlaggedSchema(
    'ctms_zod_schema',
    businessLicenseYupSchema,
    businessLicenseSchema,
  );
  const initialValues = useInitialValues(schema, data);
  const validationSchema = useMemo(
    () => toFormikValidationSchema(schema),
    [schema],
  );

  const formik = useFormikEnhanced<BusinessLicenseDTO, APIResponse>({
    initialValues,
    validationSchema,
    onSubmit: updateBusinessLicense,
    onSubmitSuccess: () => {
      addSnackbar('Business License was successfully updated', {
        variant: 'success',
      });

      trackCarrierProfileEvent({
        name: 'Carrier Updated Carrier Info',
        utm_medium: 'Carrier Profile',
        utm_content: 'Business License',
      });
    },
    onSubmitFailure: ({ message }: APIError) => {
      addSnackbar(`Failed to update Business License: ${message}`, {
        variant: 'error',
      });
    },
  });

  useEffect(() => {
    if (isError) {
      addSnackbar('Failed to fetch Business License', { variant: 'error' });
    }
  }, [isError, addSnackbar]);

  const submitVerificationForm = useSubmitVerificationDocument(formik);

  return (
    <>
      <DocumentTitle title="Business License - Profile" />

      <VerificationDialogs formik={formik} />

      <ProfilePageLayout
        formik={formik}
        title="Business License"
        isLoading={isLoading}
        actions={
          <Button
            type="button"
            onClick={submitVerificationForm}
            pending={formik.isSubmitting}
            disabled={!formik.dirty}
            fullWidth={isWebView}
          >
            Save
          </Button>
        }
      >
        <ProfileStateMotorCarrierPermitForm />
      </ProfilePageLayout>
    </>
  );
}
